"use client";

import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { CreditCard, Info, Wallet } from "lucide-react";
import type { Pricing } from "@/lib/types/booking";

interface PaymentModalProps {
  pricing: Pricing;
  payPartial: boolean;
  payAmount: number;
  loading: boolean;
  onClose: () => void;
  onPayPartialChange: (checked: boolean) => void;
  onStartPayment: () => void;
}

function useFormatMoney() {
  const { i18n } = useTranslation();
  return React.useCallback(
    (value: number) =>
      new Intl.NumberFormat(i18n.language?.startsWith("es") ? "es-AR" : "en-US", {
        style: "currency",
        currency: "ARS",
        maximumFractionDigits: 0,
      }).format(value),
    [i18n.language]
  );
}

export function PaymentModal({
  pricing,
  payPartial,
  payAmount,
  loading,
  onClose,
  onPayPartialChange,
  onStartPayment,
}: PaymentModalProps) {
  const { t } = useTranslation();
  const formatMoney = useFormatMoney();

  const remainingAfterThisPayment =
    payPartial && pricing.totalAmount > 0
      ? Math.max(0, pricing.totalAmount - payAmount)
      : 0;

  const toggleTrack = payPartial ? "bg-primary" : "bg-slate-200";
  const toggleThumb = payPartial ? "translate-x-5" : "translate-x-0";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end justify-center overflow-x-hidden overflow-y-auto bg-slate-950/55 p-0 backdrop-blur-[6px] sm:items-center sm:p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: 12 }}
        transition={{ type: "spring", duration: 0.42, bounce: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="relative flex max-h-[min(92dvh,720px)] w-full max-w-[420px] min-w-0 flex-col overflow-hidden rounded-t-[1.35rem] border border-slate-200/80 border-b-0 bg-white shadow-[0_32px_64px_-20px_rgba(15,23,42,0.35)] sm:my-4 sm:rounded-2xl sm:border-b"
      >
        <div
          className="h-1 w-full shrink-0 bg-linear-to-r from-[#2248d6] via-[#3b63e8] to-[#2248d6]"
          aria-hidden
        />

        <div className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-y-contain px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-5 sm:px-7 sm:pb-7 sm:pt-7">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary sm:h-11 sm:w-11">
              <Wallet className="h-[1.15rem] w-[1.15rem] sm:h-5 sm:w-5" strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-balance text-lg font-semibold tracking-tight text-slate-900 sm:text-xl">
                {t("payment.title")}
              </h3>
              <p className="mt-1 text-pretty text-sm leading-relaxed text-slate-500">
                {t("payment.subtitle")}
              </p>
            </div>
          </div>

          <div className="mt-5 rounded-2xl border border-slate-200/90 bg-slate-50/80 p-4 sm:mt-6 sm:p-5">
            <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-500 sm:text-xs">
              {t("payment.amountLabel")}
            </p>
            <p className="mt-2 break-words font-mono text-[clamp(1.5rem,7vw,2rem)] font-semibold leading-tight tabular-nums tracking-tight text-slate-900">
              {formatMoney(payAmount)}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 font-medium text-slate-600 ring-1 ring-slate-200/80">
                <CreditCard className="h-3.5 w-3.5 text-primary" aria-hidden />
                {payPartial ? t("payment.typePartial") : t("payment.typeFull")}
              </span>
              {payPartial && remainingAfterThisPayment > 0 && (
                <span className="text-slate-600">
                  {t("payment.remainingHint", {
                    amount: formatMoney(remainingAfterThisPayment),
                  })}
                </span>
              )}
            </div>
          </div>

          <div className="mt-5 sm:mt-5">
            <div
              role="switch"
              tabIndex={0}
              aria-checked={payPartial}
              aria-label={t("payment.payPartial")}
              onClick={() => onPayPartialChange(!payPartial)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onPayPartialChange(!payPartial);
                }
              }}
              className="flex cursor-pointer flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm outline-none transition hover:border-slate-300 hover:shadow-md focus-visible:ring-2 focus-visible:ring-primary/35 focus-visible:ring-offset-2 sm:flex-row sm:items-start sm:gap-4"
            >
              <div className="min-w-0 flex-1 pt-0.5">
                <div className="flex items-start justify-between gap-3 sm:block">
                  <span className="min-w-0 flex-1 text-sm font-semibold text-slate-900">
                    {t("payment.payPartial")}
                  </span>
                  <span
                    aria-hidden
                    className={`relative mt-0.5 inline-flex h-7 w-12 shrink-0 rounded-full transition-colors sm:hidden ${toggleTrack}`}
                  >
                    <span
                      className={`pointer-events-none absolute top-0.5 left-0.5 inline-block h-6 w-6 rounded-full bg-white shadow transition-transform ${toggleThumb}`}
                    />
                  </span>
                </div>
                <p className="mt-1 text-pretty text-sm leading-snug text-slate-500">
                  {t("payment.payPartialDescription")}
                </p>
              </div>
              <span
                aria-hidden
                className={`relative mt-0.5 hidden h-7 w-12 shrink-0 rounded-full transition-colors sm:inline-flex ${toggleTrack}`}
              >
                <span
                  className={`pointer-events-none absolute top-0.5 left-0.5 inline-block h-6 w-6 rounded-full bg-white shadow transition-transform ${toggleThumb}`}
                />
              </span>
            </div>
          </div>

          <div className="mt-5 flex gap-3 rounded-xl bg-primary-soft/90 px-3.5 py-3.5 text-sm leading-snug text-primary-strong sm:px-4">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
            <span className="min-w-0 font-medium">{t("payment.redirectNote")}</span>
          </div>

          <div className="mt-6 flex flex-col-reverse gap-3 sm:mt-7 sm:flex-row sm:gap-3">
            <button
              type="button"
              onClick={onClose}
              className="min-h-[48px] w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 active:bg-slate-100 sm:min-h-[44px] sm:flex-1"
            >
              {t("summary.volver")}
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={onStartPayment}
              className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white shadow-[0_4px_14px_-4px_rgba(34,72,214,0.55)] transition hover:bg-[#1c3fc0] active:bg-[#1938a8] disabled:opacity-60 sm:min-h-[44px] sm:flex-1"
            >
              {loading ? (
                t("payment.processing")
              ) : (
                <>
                  <span className="truncate">{t("payment.mercadopago")}</span>
                  <CreditCard className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
